import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Plus, Pencil, Trash2, Ticket, Copy, MapPin, Calendar as CalIcon, QrCode, X,
  ExternalLink, ScanLine, CheckCircle2, Camera, Keyboard,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Scanner } from "@yudiel/react-qr-scanner";

const emptyEvent = () => ({
  name: "",
  description: "",
  venue: "",
  starts_at: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 16),
  image_url: "",
  is_published: true,
  categories: [{ name: "Tribune", price_xof: 2000, quantity: 100, description: "" }],
});

export default function Ticketing() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyEvent());
  const [tab, setTab] = useState("events");

  // Orders + Scanner
  const [selectedEventId, setSelectedEventId] = useState("");
  const [orders, setOrders] = useState([]);
  const [scanCode, setScanCode] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [scanMode, setScanMode] = useState("manual"); // 'manual' | 'camera'
  const [cameraActive, setCameraActive] = useState(false);
  const [lastScanned, setLastScanned] = useState({ code: "", at: 0 });

  const load = () => api.get("/ticketing/events").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!selectedEventId) { setOrders([]); return; }
    api.get(`/ticketing/events/${selectedEventId}/orders`).then(({ data }) => setOrders(data));
  }, [selectedEventId]);

  const slug = user?.club?.slug || "";
  const publicLink = (eid) => `${window.location.origin}/billetterie/${slug}/${eid}`;

  const openCreate = () => { setEditing(null); setForm(emptyEvent()); setOpen(true); };
  const openEdit = (ev) => {
    setEditing(ev);
    setForm({
      name: ev.name || "",
      description: ev.description || "",
      venue: ev.venue || "",
      starts_at: ev.starts_at ? ev.starts_at.slice(0, 16) : "",
      image_url: ev.image_url || "",
      is_published: ev.is_published !== false,
      categories: (ev.categories || []).map((c) => ({
        id: c.id, name: c.name, price_xof: c.price_xof, quantity: c.quantity, description: c.description || "",
      })),
    });
    setOpen(true);
  };

  const addCat = () => setForm((f) => ({
    ...f, categories: [...f.categories, { name: "", price_xof: 1000, quantity: 50, description: "" }],
  }));
  const updateCat = (i, patch) => setForm((f) => ({
    ...f, categories: f.categories.map((c, idx) => idx === i ? { ...c, ...patch } : c),
  }));
  const removeCat = (i) => setForm((f) => ({
    ...f, categories: f.categories.filter((_, idx) => idx !== i),
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.starts_at) { toast.error("Nom et date requis"); return; }
    if (!form.categories.length) { toast.error("Au moins une catégorie de billet"); return; }
    const payload = {
      ...form,
      categories: form.categories.map((c) => ({
        ...c,
        price_xof: parseInt(c.price_xof, 10),
        quantity: parseInt(c.quantity, 10),
        description: c.description || null,
      })),
      description: form.description || null,
      venue: form.venue || null,
      image_url: form.image_url || null,
    };
    try {
      if (editing) {
        await api.patch(`/ticketing/events/${editing.id}`, payload);
        toast.success("Événement modifié");
      } else {
        await api.post("/ticketing/events", payload);
        toast.success("Événement créé");
      }
      setOpen(false); setEditing(null); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const onDelete = async (ev) => {
    if (!window.confirm(`Supprimer « ${ev.name} » ? Les commandes payées ne seront pas supprimées des statistiques mais les billets resteront valides.`)) return;
    try {
      await api.delete(`/ticketing/events/${ev.id}`);
      toast.success("Supprimé");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const copyLink = (eid) => {
    navigator.clipboard.writeText(publicLink(eid));
    toast.success("Lien copié");
  };

  const onScan = async (e) => {
    e?.preventDefault();
    const code = (scanCode || "").trim();
    if (!code) return;
    await submitScan(code);
    setScanCode("");
  };

  const submitScan = async (code) => {
    try {
      const { data } = await api.post("/ticketing/scan", { code });
      setScanResult(data);
      if (data.ok) {
        toast.success("Billet validé");
        // Brief visual/audio feedback for camera mode
        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          const o = ctx.createOscillator();
          o.type = "sine"; o.frequency.value = 880;
          o.connect(ctx.destination); o.start(); o.stop(ctx.currentTime + 0.15);
        } catch (_) { /* no audio */ }
      } else {
        toast.error(data.reason || "Billet invalide");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const onCameraDetect = (detectedCodes) => {
    if (!detectedCodes || detectedCodes.length === 0) return;
    const value = (detectedCodes[0].rawValue || "").trim();
    if (!value) return;
    // Debounce: don't re-submit the same code within 3 seconds
    const now = Date.now();
    if (value === lastScanned.code && now - lastScanned.at < 3000) return;
    setLastScanned({ code: value, at: now });
    submitScan(value);
  };

  const fmtFCFA = (n) => `${(n || 0).toLocaleString("fr-FR")} FCFA`;

  return (
    <div>
      <PageHeader
        overline="Billetterie"
        title="Billetterie en ligne"
        description="Vendez des billets en ligne via FedaPay (Mobile Money + cartes bancaires)."
        actions={
          <Button onClick={openCreate} data-testid="create-ticketing-event-btn" className="btn-club">
            <Plus className="w-4 h-4 mr-2" /> Nouvel événement
          </Button>
        }
      />

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="events" data-testid="tab-tk-events">Événements ({list.length})</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-tk-orders">Commandes</TabsTrigger>
          <TabsTrigger value="scanner" data-testid="tab-tk-scanner">Scanner</TabsTrigger>
        </TabsList>

        <TabsContent value="events">
          {list.length === 0 ? (
            <div className="bg-white border border-dashed border-[#E0E0E0] rounded-md p-12 text-center">
              <Ticket className="w-10 h-10 text-[#C62828]/40 mx-auto mb-4" />
              <h3 className="font-display text-xl font-bold">Aucun événement</h3>
              <p className="text-sm text-[#616161] mt-2">Créez votre premier événement pour vendre des billets en ligne.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ticketing-events-grid">
              {list.map((ev) => {
                const totalQty = (ev.categories || []).reduce((s, c) => s + (c.quantity || 0), 0);
                const sold = (ev.categories || []).reduce((s, c) => s + (c.sold || 0), 0);
                return (
                  <div key={ev.id} className="bg-white border border-[#E0E0E0] rounded-md overflow-hidden hover:border-[var(--club-primary,#C62828)]/40 transition" data-testid={`tk-event-card-${ev.id}`}>
                    {ev.image_url ? (
                      <div className="h-32 bg-cover bg-center" style={{ backgroundImage: `url(${ev.image_url})` }} />
                    ) : (
                      <div className="h-3" style={{ background: "var(--club-primary, #C62828)" }} />
                    )}
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-1">
                        <Badge variant="outline" className={ev.is_published ? "bg-[#2E7D32]/15 text-[#2E7D32] border-[#2E7D32]/40" : "bg-[#616161]/15 text-[#616161] border-[#616161]/40"}>
                          {ev.is_published ? "Publié" : "Brouillon"}
                        </Badge>
                        <span className="text-xs text-[#616161] font-mono">{sold}/{totalQty}</span>
                      </div>
                      <h3 className="font-display text-lg font-bold truncate" title={ev.name}>{ev.name}</h3>
                      <p className="text-xs text-[#616161] mt-1 flex items-center gap-1">
                        <CalIcon className="w-3 h-3" />
                        {new Date(ev.starts_at).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" })}
                      </p>
                      {ev.venue && (
                        <p className="text-xs text-[#616161] mt-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {ev.venue}
                        </p>
                      )}
                      <div className="mt-3 flex flex-wrap gap-1">
                        {(ev.categories || []).map((c) => (
                          <Badge key={c.id} variant="outline" className="text-[10px] bg-[#FBC02D]/10 text-[#F57F17] border-[#FBC02D]/40">
                            {c.name} · {fmtFCFA(c.price_xof)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="px-4 py-2 border-t border-[#E0E0E0] flex justify-end gap-1">
                      <Button size="sm" variant="ghost" title="Copier le lien public" onClick={() => copyLink(ev.id)} data-testid={`tk-copy-${ev.id}`}>
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" title="Aperçu public" onClick={() => window.open(publicLink(ev.id), "_blank")}>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setSelectedEventId(ev.id); setTab("orders"); }} data-testid={`tk-orders-${ev.id}`}>
                        <Ticket className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(ev)} data-testid={`tk-edit-${ev.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => onDelete(ev)}>
                        <Trash2 className="w-3.5 h-3.5 text-[#C62828]" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="orders">
          <div className="bg-white border border-[#E0E0E0] rounded-md p-4 mb-3">
            <Label className="text-xs">Filtrer par événement</Label>
            <select className="mt-1 w-full border border-[#E0E0E0] rounded-md px-3 py-2 text-sm" value={selectedEventId} onChange={(e) => setSelectedEventId(e.target.value)}>
              <option value="">— Choisir un événement —</option>
              {list.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
            </select>
          </div>
          {!selectedEventId ? (
            <p className="text-sm text-[#616161] p-8 text-center">Sélectionnez un événement pour voir les commandes.</p>
          ) : orders.length === 0 ? (
            <div className="bg-white border border-dashed border-[#E0E0E0] rounded-md p-8 text-center">
              <Ticket className="w-8 h-8 text-[#616161]/40 mx-auto mb-2" />
              <p className="text-sm text-[#616161]">Aucune commande pour cet événement.</p>
            </div>
          ) : (
            <div className="bg-white border border-[#E0E0E0] rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F5F7] text-[#616161] text-[10px] tracking-overline">
                  <tr>
                    <th className="text-left px-4 py-2.5">Acheteur</th>
                    <th className="text-left px-4 py-2.5">Catégorie</th>
                    <th className="text-right px-4 py-2.5">Qté</th>
                    <th className="text-right px-4 py-2.5">Total</th>
                    <th className="text-center px-4 py-2.5">Statut</th>
                    <th className="text-left px-4 py-2.5">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E0E0E0]">
                  {orders.map((o) => (
                    <tr key={o.id} className="hover:bg-[#F4F5F7]/50">
                      <td className="px-4 py-2.5">
                        <p className="font-medium">{o.buyer_name}</p>
                        <p className="text-[11px] text-[#616161]">{o.buyer_email} · {o.buyer_phone}</p>
                      </td>
                      <td className="px-4 py-2.5">{o.category_name}</td>
                      <td className="px-4 py-2.5 text-right">{o.quantity}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{fmtFCFA(o.total_xof)}</td>
                      <td className="px-4 py-2.5 text-center">
                        <Badge variant="outline" className={
                          o.status === "paid" ? "bg-[#2E7D32]/15 text-[#2E7D32] border-[#2E7D32]/40" :
                            o.status === "failed" ? "bg-[#C62828]/15 text-[#C62828] border-[#C62828]/40" :
                              "bg-[#FBC02D]/15 text-[#F57F17] border-[#FBC02D]/40"
                        }>{o.status}</Badge>
                      </td>
                      <td className="px-4 py-2.5 text-[11px] text-[#616161]">
                        {new Date(o.created_at).toLocaleString("fr-FR")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="scanner">
          <div className="max-w-md mx-auto">
            <div className="bg-white border border-[#E0E0E0] rounded-md p-6">
              <h3 className="font-display text-lg font-bold flex items-center gap-2 mb-3">
                <ScanLine className="w-5 h-5" /> Scanner un billet
              </h3>

              {/* Toggle Manual / Camera */}
              <div className="flex gap-2 mb-4 bg-[#F4F5F7] p-1 rounded-md">
                <button
                  type="button"
                  onClick={() => { setScanMode("manual"); setCameraActive(false); }}
                  className={`flex-1 px-3 py-2 rounded text-sm font-medium flex items-center justify-center gap-1.5 transition ${
                    scanMode === "manual" ? "bg-white text-[#121212] shadow-sm" : "text-[#616161] hover:text-[#121212]"
                  }`}
                  data-testid="scan-mode-manual"
                >
                  <Keyboard className="w-4 h-4" /> Saisie
                </button>
                <button
                  type="button"
                  onClick={() => setScanMode("camera")}
                  className={`flex-1 px-3 py-2 rounded text-sm font-medium flex items-center justify-center gap-1.5 transition ${
                    scanMode === "camera" ? "bg-white text-[#121212] shadow-sm" : "text-[#616161] hover:text-[#121212]"
                  }`}
                  data-testid="scan-mode-camera"
                >
                  <Camera className="w-4 h-4" /> Caméra
                </button>
              </div>

              {scanMode === "manual" && (
                <form onSubmit={onScan} className="space-y-3">
                  <p className="text-xs text-[#616161]">Saisissez le code du billet (ex: WSDK-AB12CD34).</p>
                  <Input
                    value={scanCode}
                    onChange={(e) => setScanCode(e.target.value)}
                    placeholder="WSDK-XXXXXXXX ou ID complet"
                    className="font-mono"
                    data-testid="scan-input"
                    autoFocus
                  />
                  <Button type="submit" className="btn-club w-full" data-testid="scan-submit">
                    Valider l&apos;entrée
                  </Button>
                </form>
              )}

              {scanMode === "camera" && (
                <div className="space-y-3">
                  {!cameraActive ? (
                    <div className="bg-[#F4F5F7] border-2 border-dashed border-[#E0E0E0] rounded-md p-8 text-center">
                      <Camera className="w-12 h-12 text-[#9E9E9E] mx-auto mb-3" />
                      <p className="text-sm text-[#616161] mb-4">
                        Activez la caméra pour scanner les QR codes des billets.
                      </p>
                      <Button onClick={() => setCameraActive(true)} className="btn-club" data-testid="start-camera-btn">
                        <Camera className="w-4 h-4 mr-2" /> Démarrer la caméra
                      </Button>
                      <p className="text-[10px] text-[#9E9E9E] mt-3">
                        Votre navigateur demandera l&apos;accès à la caméra.<br/>
                        Préférez l&apos;arrière pour scanner les billets.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="relative rounded-md overflow-hidden bg-black aspect-square border-2 border-[var(--club-primary,#C62828)]" data-testid="qr-scanner-video">
                        <Scanner
                          onScan={onCameraDetect}
                          onError={(e) => console.error("Scanner error:", e)}
                          constraints={{ facingMode: "environment" }}
                          formats={["qr_code"]}
                          components={{ finder: true, audio: false }}
                          styles={{ container: { width: "100%", height: "100%" } }}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setCameraActive(false)}
                        className="w-full"
                        data-testid="stop-camera-btn"
                      >
                        <X className="w-4 h-4 mr-2" /> Arrêter la caméra
                      </Button>
                      <p className="text-[11px] text-[#616161] text-center">
                        Pointez la caméra vers le QR code du billet — la validation est automatique.
                      </p>
                    </>
                  )}
                </div>
              )}

              {scanResult && (
                <div className={`mt-4 p-3 rounded border ${scanResult.ok ? "bg-[#2E7D32]/10 border-[#2E7D32]/30" : "bg-[#C62828]/10 border-[#C62828]/30"}`} data-testid="scan-result">
                  <div className="flex items-start gap-2">
                    {scanResult.ok ? (
                      <CheckCircle2 className="w-5 h-5 text-[#2E7D32] flex-shrink-0 mt-0.5" />
                    ) : (
                      <X className="w-5 h-5 text-[#C62828] flex-shrink-0 mt-0.5" />
                    )}
                    <div className="text-sm flex-1">
                      <p className="font-semibold">
                        {scanResult.ok ? "Billet validé" : `Refusé — ${scanResult.reason}`}
                      </p>
                      {scanResult.ticket && (
                        <>
                          <p className="text-[11px] text-[#616161] mt-1">Code : <span className="font-mono">{scanResult.ticket.code}</span></p>
                          <p className="text-[11px] text-[#616161]">Acheteur : {scanResult.ticket.buyer_name}</p>
                          {scanResult.scanned_at && (
                            <p className="text-[11px] text-[#C62828] font-medium">
                              Déjà scanné le {new Date(scanResult.scanned_at).toLocaleString("fr-FR")}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyEvent()); } }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="ticketing-event-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Modifier l'événement" : "Nouvel événement"}</DialogTitle>
            <DialogDescription>
              Configurez vos catégories de billets. Le lien public sera : <code className="text-xs bg-[#F4F5F7] px-1.5 py-0.5 rounded">{window.location.origin}/billetterie/{slug}/...</code>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label>Nom de l&apos;événement *</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tk-name" />
              </div>
              <div>
                <Label>Date et heure *</Label>
                <Input type="datetime-local" required value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="tk-starts-at" />
              </div>
              <div>
                <Label>Lieu / Stade</Label>
                <Input value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Description</Label>
                <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>URL de l&apos;image (affiche)</Label>
                <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." />
              </div>
              <div className="md:col-span-2 flex items-center gap-2">
                <input id="tk-pub" type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} className="w-4 h-4" />
                <Label htmlFor="tk-pub" className="cursor-pointer">Publier (accessible publiquement)</Label>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs tracking-overline text-[#616161]">Catégories de billets</h3>
                <Button type="button" variant="outline" size="sm" onClick={addCat} data-testid="tk-add-cat">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter une catégorie
                </Button>
              </div>
              <div className="space-y-2">
                {form.categories.map((c, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end p-2 bg-[#F4F5F7] rounded border border-[#E0E0E0]">
                    <div className="col-span-4">
                      <Label className="text-[10px]">Nom *</Label>
                      <Input required value={c.name} onChange={(e) => updateCat(i, { name: e.target.value })} className="h-8" placeholder="Tribune, VIP..." />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px]">Prix (FCFA) *</Label>
                      <Input type="number" min="0" required value={c.price_xof} onChange={(e) => updateCat(i, { price_xof: e.target.value })} className="h-8" />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px]">Quantité *</Label>
                      <Input type="number" min="1" required value={c.quantity} onChange={(e) => updateCat(i, { quantity: e.target.value })} className="h-8" />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px]">Description</Label>
                      <Input value={c.description || ""} onChange={(e) => updateCat(i, { description: e.target.value })} className="h-8" />
                    </div>
                    <div className="col-span-1">
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeCat(i)} disabled={form.categories.length === 1}>
                        <X className="w-3.5 h-3.5 text-[#C62828]" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" className="btn-club" data-testid="tk-submit-btn">{editing ? "Enregistrer" : "Créer"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
